import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminCommonSteelVersion,
  InsertCommonSteelVersionBody,
  UpdateCommonSteelVersionNewImageBody,
  UpdateCommonSteelVersionRemoveImageBody,
} from "../types/commonSteelVersions";

export const usePatrolSteelVersionsApi = () => {
  const api = useApi();

  const getAdminSteelVersion = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/steel_versions`;
      return useSWRMutation<AdminCommonSteelVersion, CustomError, string, string>(
        url,
        (url, { arg }: { arg: string }) => api.get<AdminCommonSteelVersion>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminSteelVersion = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/steel_versions", query);
      return useSWR<Paginated<AdminCommonSteelVersion>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminCommonSteelVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminSteelVersion = useCallback(
    async (body: InsertCommonSteelVersionBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/steel_versions`;
      const id = await api.post<{ id: string }, InsertCommonSteelVersionBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminSteelVersion = useCallback(
    async (
      supportMaterialsd: string,
      body: UpdateCommonSteelVersionRemoveImageBody | UpdateCommonSteelVersionNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/patrol/steel_versions/${supportMaterialsd}`;
      (await api.put<UpdateCommonSteelVersionRemoveImageBody | UpdateCommonSteelVersionNewImageBody>(url, body),
        mutateCache(["/admin/settings/patrol/steel_versions"]));
    },
    [api],
  );

  // const deleteAdminMaterial = useCallback(
  //   async (supportMaterialsd: string): Promise<void> => {
  //     const url = `/admin/settings/patrol/support_materials/${supportMaterialsd}`;
  //     await api.del(url)
  //       mutateCache([`/admin/settings/patrol/support_materials`]);
  //   },
  //   [api]
  // );

  // const generateUploadImageUrl = useCallback(
  //   async (supportMaterialsd: string, fileName: string, fileType: string): Promise<string> => {
  //     const encodedFileName = encodeURIComponent(fileName);
  //     const url = `/admin/settings/patrol/support_materials/${supportMaterialsd}/image/generate_upload_url?file_name=${encodedFileName}`;
  //    return api.post<string, GenerateUrlRequestBody>(url, {
  //   filename: fileName,
  //   contentType: fileType,
  // });
  //   },
  //   [api]
  // );

  const updateAdminSteelVersionVisibility = useCallback(
    async (steelVersionId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/steel_versions/${steelVersionId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/steel_versions"]);
    },
    [api],
  );

  const updateAdminSteelVersionOrder = useCallback(
    async (steelVersionId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/steel_versions/${steelVersionId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/steel_versions"]);
    },
    [api],
  );

  return {
    getAdminMaterial: getAdminSteelVersion,
    useAdminMaterials: useAdminSteelVersion,
    insertAdminMaterial: insertAdminSteelVersion,
    updateAdminMaterial: updateAdminSteelVersion,
    updateAdminSteelVersionVisibility,
    updateAdminSteelVersionOrder,
    // deleteAdminMaterial,
    // generateUploadImageUrl,
  };
};
